import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { sleep } from 'utils/utils';

export const NETWORK_ERROR_MESSAGES = [
  'failed to fetch',
  'network error',
  'NetworkError when attempting to fetch resource', // firefox
  'fetch failed: Load failed', // safari
];
export const NETWORK_GUARD_ERROR_MESSAGE = 'Network guard has detected an error';
const RETRY_DELAY_MS = 1000;

export type NetworkErrorGuardFn = <T>(fn: () => Promise<T>, retries?: number) => Promise<T>;

const getIsLikelyNetworkError = (error: unknown) => {
  const isAbortError = error instanceof Object && 'name' in error && error.name === 'AbortError';
  const isFetchError = NETWORK_ERROR_MESSAGES.some(
    (message) =>
      error instanceof Object &&
      'message' in error &&
      typeof error.message === 'string' &&
      error.message.toLowerCase().includes(message.toLowerCase())
  );

  return isFetchError || isAbortError;
};

const getIsHttpServerError = (error: unknown): boolean =>
  error instanceof Object &&
  'httpStatus' in error &&
  typeof (error as { httpStatus: unknown }).httpStatus === 'number' &&
  (error as { httpStatus: number }).httpStatus >= 500;

const useNetworkErrorGuard = () => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const runWithNetworkGuard: NetworkErrorGuardFn = useCallback(
    async (fn, retries = 3) => {
      try {
        const result = await fn();
        return result;
      } catch (error) {
        if (getIsLikelyNetworkError(error)) {
          if (retries <= 1) {
            navigate('/network-error', { state: { from: pathname + search } });
            throw new Error(NETWORK_GUARD_ERROR_MESSAGE);
          }

          await sleep(RETRY_DELAY_MS);
          return await runWithNetworkGuard(fn, retries - 1);
        }

        if (getIsHttpServerError(error)) {
          if (retries <= 1) throw error;

          await sleep(RETRY_DELAY_MS);
          return await runWithNetworkGuard(fn, retries - 1);
        }

        throw error;
      }
    },
    [navigate, pathname, search]
  );

  return { runWithNetworkGuard };
};

export default useNetworkErrorGuard;
